import { useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import CTASection from "@/components/CTASection";
import { SERVICES, SERVICE_CATEGORIES } from "@/data/content";

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

export default function Services() {
  const [active, setActive] = useState("all");
  const list = active === "all" ? SERVICES : SERVICES.filter((s) => s.category === active);

  return (
    <>
      <SEO title="Services" description="Enterprise consulting services across SQL Server, PostgreSQL, Azure SQL, database performance engineering, cloud migration, HA/DR, data engineering and managed operations." />

      <section className="bg-[var(--dm-navy)] text-white">
        <div className="dm-container py-24 lg:py-32">
          <div className="dm-overline text-[var(--dm-emerald)]">Services</div>
          <h1 className="font-serif text-5xl sm:text-6xl leading-[1.05] mt-6 max-w-4xl">A complete catalogue of enterprise database and data services.</h1>
          <p className="text-white/70 text-lg mt-8 max-w-3xl leading-relaxed">Every organization has unique technical requirements. We begin every engagement with an initial technical discovery discussion, followed by an assessment of your environment. Based on our findings we prepare a customized proposal tailored specifically to your infrastructure, business goals and project scope.</p>
        </div>
      </section>

      <section className="bg-white sticky top-20 z-30 border-b border-[var(--dm-border)]">
        <div className="dm-container py-4 flex gap-2 overflow-x-auto">
          {[{id:"all", name:"All"}, ...SERVICE_CATEGORIES].map((c) => (
            <button
              key={c.id}
              data-testid={`svc-filter-${c.id}`}
              onClick={() => setActive(c.id)}
              className={`px-4 py-2 text-xs uppercase tracking-[0.18em] font-semibold whitespace-nowrap transition-colors ${active === c.id ? 'bg-[var(--dm-navy)] text-white' : 'text-[var(--dm-muted)] hover:text-[var(--dm-navy)]'}`}
            >{c.name}</button>
          ))}
        </div>
      </section>

      <section className="bg-white">
        <div className="dm-container dm-section">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--dm-border)]">
            {list.map((s) => (
              <Link key={s.slug} to={`/services/${s.slug}`} data-testid={`svc-card-${s.slug}`} className="group bg-white p-8 hover:bg-[var(--dm-surface)] transition-colors flex flex-col min-h-[280px]">
                <div className="w-10 h-10 grid place-items-center bg-[var(--dm-navy)] text-white mb-6">
                  <Icon name={s.icon} className="w-5 h-5" />
                </div>
                <div className="font-serif text-xl text-[var(--dm-navy)] leading-snug">{s.name}</div>
                <div className="text-sm text-[var(--dm-muted)] mt-3 leading-relaxed">{s.tagline}</div>
                <div className="mt-auto pt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[var(--dm-emerald)]">
                  Explore <ArrowUpRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </>
  );
}
